// @ts-check

import { html } from "lit-html";
import { renderErrorMessage } from "../ui.js";
import { copyTextToClipboard, hasText } from "../utils.js";

/** @typedef {ReturnType<typeof html>} LitView */
/** @typedef {import("./index.js").ToolEnv} ToolEnv */
/** @typedef {ReturnType<typeof createBase64State>} Base64State */

const TOOL_FRAME_STYLE =
  "height: calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 9rem);";
const TOOL_FRAME_CLASS = "flex min-w-0 flex-col gap-3";
const TEXTAREA_GRID_CLASS =
  "grid min-h-0 flex-1 grid-rows-2 gap-3 sm:grid-cols-2 sm:grid-rows-1";
const TEXTAREA_LABEL_CLASS =
  "grid min-h-0 grid-rows-[auto_minmax(0,1fr)] gap-1.5 text-sm font-medium text-stone-700";
const COMPACT_TEXTAREA_CLASS =
  "h-full min-h-0 w-full resize-none rounded-lg border border-stone-300 bg-white px-4 py-3 text-base leading-6 text-stone-900 outline-none transition focus:border-stone-500 focus:ring-2 focus:ring-stone-400/20";
const ACTION_GRID_CLASS = "grid gap-2";
const ACTION_ROW_CLASS = "grid grid-cols-2 gap-2";
const ICON_BUTTON_CONTENT_CLASS =
  "inline-flex min-w-0 items-center justify-center gap-1.5";
const COMPACT_PRIMARY_BUTTON_CLASS =
  "inline-flex h-10 min-w-0 items-center justify-center rounded-lg bg-stone-900 px-2 text-sm font-semibold text-white transition hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-200 disabled:text-stone-400";
const COMPACT_SECONDARY_BUTTON_CLASS =
  "inline-flex h-10 min-w-0 items-center justify-center rounded-lg border border-stone-300 bg-white px-2 text-sm font-semibold text-stone-700 transition hover:border-stone-400 hover:bg-stone-50 disabled:cursor-not-allowed disabled:border-stone-200 disabled:bg-white disabled:text-stone-300";

export const base64Tool = {
  key: "base64",
  path: "/base64",
  title: "Base64 Encode / Decode",
  blurb: "Encode text to Base64 or decode Base64 back to text.",
  badge: "Text",
  accentClass: "bg-gradient-to-r from-lime-300 via-emerald-300 to-cyan-300",
  createState: createBase64State,
  render: renderBase64Page,
};

function createBase64State() {
  return {
    input: "",
    output: "",
    error: "",
    copyStatus: "idle",
  };
}

/**
 * @param {Base64State} toolState
 * @param {ToolEnv} env
 * @returns {LitView}
 */
function renderBase64Page(toolState, env) {
  const canCopy = hasText(toolState.output);
  const copyButtonClass = getCopyButtonClass(toolState.copyStatus);
  const copyButtonLabel = getCopyButtonLabel(toolState.copyStatus);

  return html`
    <section class=${TOOL_FRAME_CLASS} style=${TOOL_FRAME_STYLE}>
      <div class=${TEXTAREA_GRID_CLASS}>
        <label class=${TEXTAREA_LABEL_CLASS}>
          <span>Input</span>
          <textarea
            class=${COMPACT_TEXTAREA_CLASS}
            .value=${toolState.input}
            placeholder="Paste text or Base64 here..."
            @input=${
              /**
               * @param {Event} event
               * @returns {void}
               */
              function (event) {
                env.handleFieldInput(toolState, "input", event);
              }
            }
          ></textarea>
        </label>
        <label class=${TEXTAREA_LABEL_CLASS}>
          <span>Output</span>
          <textarea
            class=${COMPACT_TEXTAREA_CLASS}
            .value=${toolState.output}
            placeholder="Result appears here..."
            @input=${
              /**
               * @param {Event} event
               * @returns {void}
               */
              function (event) {
                env.handleFieldInput(toolState, "output", event);
              }
            }
          ></textarea>
        </label>
      </div>

      ${toolState.error ? renderErrorMessage(toolState.error) : ""}

      <div class=${ACTION_GRID_CLASS}>
        <div class=${ACTION_ROW_CLASS}>
          <button
            class=${COMPACT_PRIMARY_BUTTON_CLASS}
            type="button"
            @click=${function () {
              applyBase64Encode(toolState);
              env.scheduleRender();
            }}
          >
            Encode
          </button>
          <button
            class=${COMPACT_SECONDARY_BUTTON_CLASS}
            type="button"
            @click=${function () {
              applyBase64Decode(toolState);
              env.scheduleRender();
            }}
          >
            Decode
          </button>
        </div>
        <div class=${ACTION_ROW_CLASS}>
          <button
            class=${copyButtonClass}
            type="button"
            ?disabled=${!canCopy}
            @click=${async function () {
              await copyOutput(toolState, env, "base64");
            }}
          >
            <span class=${ICON_BUTTON_CONTENT_CLASS}>
              ${renderCopyIcon()}
              <span>${copyButtonLabel}</span>
            </span>
          </button>
          <button
            class=${COMPACT_SECONDARY_BUTTON_CLASS}
            type="button"
            @click=${function () {
              toolState.input = "";
              toolState.output = "";
              toolState.error = "";
              toolState.copyStatus = "idle";
              env.scheduleRender();
            }}
          >
            <span class=${ICON_BUTTON_CONTENT_CLASS}>
              ${renderClearIcon()}
              <span>Clear</span>
            </span>
          </button>
        </div>
      </div>
    </section>
  `;
}

/**
 * @returns {LitView}
 */
function renderCopyIcon() {
  return html`
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      class="size-6 shrink-0"
      aria-hidden="true"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
      />
    </svg>
  `;
}

/**
 * @returns {LitView}
 */
function renderClearIcon() {
  return html`
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      class="size-6 shrink-0"
      aria-hidden="true"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  `;
}

/**
 * @param {Base64State} toolState
 * @returns {void}
 */
function applyBase64Encode(toolState) {
  toolState.error = "";
  toolState.copyStatus = "idle";

  try {
    const bytes = new TextEncoder().encode(toolState.input);
    const binaryText = bytesToBinaryText(bytes);
    toolState.output = btoa(binaryText);
  } catch (error) {
    console.error("[app] base64 encode failed", error);
    toolState.output = "";
    toolState.error = "Could not encode this text.";
  }
}

/**
 * @param {Base64State} toolState
 * @returns {void}
 */
function applyBase64Decode(toolState) {
  toolState.error = "";
  toolState.copyStatus = "idle";

  try {
    const normalizedInput = toolState.input.replace(/\s+/g, "");
    const binaryText = atob(normalizedInput);
    const bytes = Uint8Array.from(binaryText, function (character) {
      return character.codePointAt(0) || 0;
    });
    toolState.output = new TextDecoder().decode(bytes);
  } catch (error) {
    console.error("[app] base64 decode failed", error);
    toolState.output = "";
    toolState.error = "Enter valid Base64 to decode.";
  }
}

/**
 * @param {Uint8Array} bytes
 * @returns {string}
 */
function bytesToBinaryText(bytes) {
  const chunkSize = 8192;
  let result = "";

  for (let index = 0, total = bytes.length; index < total; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    result += String.fromCodePoint(...chunk);
  }

  return result;
}

/**
 * @param {Base64State} toolState
 * @param {ToolEnv} env
 * @param {string} timerKey
 * @returns {Promise<void>}
 */
async function copyOutput(toolState, env, timerKey) {
  if (!hasText(toolState.output)) {
    return;
  }

  try {
    await copyTextToClipboard(toolState.output);
    env.setTemporaryStatus(toolState, "copyStatus", "copied", "idle", timerKey);
  } catch (error) {
    console.error("[app] copy output failed", error);
    env.setTemporaryStatus(toolState, "copyStatus", "error", "idle", timerKey);
  }
}

/**
 * @param {string} copyStatus
 * @returns {string}
 */
function getCopyButtonClass(copyStatus) {
  let result = COMPACT_SECONDARY_BUTTON_CLASS;
  if (copyStatus === "copied") {
    result =
      "inline-flex h-10 min-w-0 items-center justify-center rounded-lg bg-emerald-700 px-2 text-sm font-semibold text-white";
  }

  if (copyStatus === "error") {
    result =
      "inline-flex h-10 min-w-0 items-center justify-center rounded-lg bg-red-600 px-2 text-sm font-semibold text-white";
  }

  return result;
}

/**
 * @param {string} copyStatus
 * @returns {string}
 */
function getCopyButtonLabel(copyStatus) {
  let result = "Copy";
  if (copyStatus === "copied") {
    result = "Copied";
  }

  if (copyStatus === "error") {
    result = "Copy failed";
  }

  return result;
}
